import { useRememberDocument } from '@/contexts/DocumentWorkspace';
import { useIncomingDocument } from '@/contexts/DocumentWorkspace';
import { useState, useCallback, useRef } from 'react';
import { useWorkspaceNavigate as useNavigate } from '@/contexts/DocumentWorkspace';
import {
  Upload,
  Layers,
  Loader2,
  Download,
  Eye,
  ArrowRight,
  CheckCircle,
  FileCheck,
  AlertTriangle,
} from 'lucide-react';
import { pdfjsLib } from '@/lib/pdfWorker';
import { readPdfLibBytes, pdfIntakeErrorMessage } from '@/lib/pdfIntake';
import { analyzePDF, flattenPDF, formatFileSize } from '@/lib/pdfOperations';
import { useFileDialog } from '@/hooks/useFileDialog';
import CloudSourceHint from '@/components/CloudSourceHint';
import PageCanvas from '@/components/PageCanvas';
import { ProGate } from '@/components/ProBadge';
import { usageCapApplies, hasReachedLimit, incrementUsage } from '../lib/webUsage';
import FreeTierBanner from '../components/FreeTierBanner';

/**
 * Flatten — Merge all layers (form fields, annotations, JavaScript)
 * into a single flat, static PDF.
 */
export default function Flatten() {
  const navigate = useNavigate();
  const rememberDocument = useRememberDocument();
  const { openFile } = useFileDialog();
  const loadIdRef = useRef(0);

  // File state
  const [pdfDoc, setPdfDoc] = useState(null);
  const [arrayBuffer, setArrayBuffer] = useState(null);
  const [fileName, setFileName] = useState('');
  const [fileSize, setFileSize] = useState(0);
  const [numPages, setNumPages] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  // Analysis state
  const [analysis, setAnalysis] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);

  // Options state
  const [flattenForms, setFlattenForms] = useState(true);
  const [flattenAnnotations, setFlattenAnnotations] = useState(true);
  const [removeJS, setRemoveJS] = useState(true);
  const [reduceFileSize, setReduceFileSize] = useState(false);

  // Processing state
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressLabel, setProgressLabel] = useState('');
  const [error, setError] = useState(null);

  // Result state
  const [result, setResult] = useState(null); // { bytes, blob, size }
  const [showFreeTierModal, setShowFreeTierModal] = useState(false);

  // ==================== File handling ====================

  const loadFile = useCallback(
    async (file) => {
      if (!file || file.type !== 'application/pdf' && !/\.pdf$/i.test(file.name)) return;
      if (pdfDoc) pdfDoc.destroy();

      const id = ++loadIdRef.current;
      setError(null);
      setResult(null);
      setAnalysis(null);
      setAnalyzing(false);
      setFileName(file.name);
      setFileSize(file.size);

      try {
        // Decrypt owner-password PDFs (lossless) / reject truly locked ones
        // before pdf-lib ever sees the bytes — see src/lib/pdfIntake.js.
        const buf = await readPdfLibBytes(file);
        if (id !== loadIdRef.current) return; // stale — user dropped another file
        setArrayBuffer(buf);
        const doc = await pdfjsLib.getDocument({ data: buf.slice(0) }).promise;
        if (id !== loadIdRef.current) { doc.destroy(); return; }
        setPdfDoc(doc);
        rememberDocument(file);
        setNumPages(doc.numPages);

        // Auto-analyze
        setAnalyzing(true);
        try {
          const analysisResult = await analyzePDF(buf, pdfjsLib);
          if (id !== loadIdRef.current) return; // stale
          setAnalysis(analysisResult);
        } catch (err) {
          if (id === loadIdRef.current) setError(`Analysis failed: ${err.message}`);
        } finally {
          if (id === loadIdRef.current) setAnalyzing(false);
        }
      } catch (err) {
        if (id === loadIdRef.current) setError(pdfIntakeErrorMessage(err, 'flatten it'));
      }
    },
    [pdfDoc, rememberDocument]
  );

  useIncomingDocument(loadFile);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    loadFile(e.dataTransfer.files[0]);
  }, [loadFile]);

  const handleBrowse = useCallback(async () => {
    const result = await openFile('.pdf');
    if (result) {
      const file = new File([result.buffer], result.name, { type: 'application/pdf' });
      loadFile(file);
    }
  }, [openFile, loadFile]);

  // ==================== Flatten ====================

  const handleFlatten = useCallback(async () => {
    if (!arrayBuffer || !pdfDoc) return;

    setProcessing(true);
    setProgress(0);
    setError(null);
    setResult(null);

    try {
      setProgressLabel('Flattening…');
      const effectiveForms = flattenForms;
      const effectiveAnnotations = flattenAnnotations;
      const effectiveJS = removeJS;

      const { bytes, changed, rasterized } = await flattenPDF(
        arrayBuffer,
        {
          flattenForms: effectiveForms,
          flattenAnnotations: effectiveAnnotations,
          removeJS: effectiveJS,
          reduceFileSize,
        },
        pdfjsLib,
        (p) => {
          setProgress(p);
          if (p < 0.15) setProgressLabel('Flattening form fields...');
          else if (p < 0.80) setProgressLabel('Preserving annotation appearances...');
          else if (p < 0.95) setProgressLabel('Rebuilding PDF...');
          else setProgressLabel('Finalizing...');
        }
      );

      const blob = new Blob([bytes], { type: 'application/pdf' });
      setResult({ bytes, blob, size: bytes.length, changed, rasterized });
      setProgress(1);
      setProgressLabel('');
    } catch (err) {
      setError(`Flatten failed: ${err.message}`);
    } finally {
      setProcessing(false);
    }
  }, [arrayBuffer, pdfDoc, flattenForms, flattenAnnotations, removeJS, reduceFileSize]);

  // ==================== Result actions ====================

  const openInViewer = useCallback(() => {
    if (!result) return;
    const baseName = fileName.replace(/\.pdf$/i, '');
    const file = new File([result.blob], `${baseName}_flat.pdf`, {
      type: 'application/pdf',
    });
    navigate('/viewer', { state: { file } });
  }, [result, fileName, navigate]);

  const handleDownload = useCallback(() => {
    if (!result) return;
    if (usageCapApplies() && hasReachedLimit()) {
      setShowFreeTierModal(true);
      return;
    }
    const baseName = fileName.replace(/\.pdf$/i, '');
    const url = URL.createObjectURL(result.blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${baseName}_flat.pdf`;
    a.click();
    URL.revokeObjectURL(url);
    if (usageCapApplies()) incrementUsage();
  }, [result, fileName]);

  // ==================== Derived ====================

  const nothingToFlatten = analysis && analysis.isAlreadyFlat;
  const anyOptionChecked = flattenForms || flattenAnnotations || removeJS || reduceFileSize;
  const actionEnabled = pdfDoc && !processing && !analyzing && anyOptionChecked;
  const reduction = result ? Math.round((1 - result.size / fileSize) * 100) : 0;
  const sizeBarWidth = result ? Math.max(5, Math.round((result.size / fileSize) * 100)) : 100;
  // Browser fallback that saved nothing new (pdf-lib couldn't load/save and
  // Stage B never ran) — the "result" is just the original bytes passed
  // through. Never present that as a success. The lossless native/cloud path
  // (result.native) never hits this branch.
  const nothingWasFlattened = result && !result.native && result.changed === false;

  // ==================== Render ====================

  return (
    <ProGate feature="flatten">
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="max-w-3xl mx-auto flex items-center gap-4">
          <div>
            <h1 className="text-xl font-bold text-gray-900">Flatten PDF</h1>
            <p className="text-sm text-gray-500">Remove layers & interactive elements</p>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto p-6">
        {/* Drop zone */}
        {!pdfDoc && (
          <>
            {/* Use case messaging */}
            <div className="text-sm text-gray-600 bg-white border border-gray-200 rounded-xl p-4 mb-6">
              <p>
                Flatten removes interactive elements (form fields, annotations, JavaScript) and
                bakes everything into a static PDF. Useful for: finalizing documents before sharing,
                ensuring PDFs look the same everywhere, reducing compatibility issues.
              </p>
            </div>

            <div
              onDrop={handleDrop}
              onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={(e) => { e.preventDefault(); setIsDragging(false); }}
              onClick={handleBrowse}
              className={`border-2 border-dashed rounded-2xl p-10 text-center cursor-pointer transition-all mb-6 ${
                isDragging
                  ? 'border-brand-blue bg-brand-light/30 scale-[1.01]'
                  : 'border-gray-300 hover:border-brand-blue hover:bg-gray-50'
              }`}
            >
              <Upload size={36} className={`mx-auto mb-3 ${isDragging ? 'text-brand-blue' : 'text-gray-400'}`} />
              <p className="text-lg font-medium text-gray-700">Drop a PDF here, or click to browse</p>
              <p className="text-sm text-gray-400 mt-1">Select a file to flatten</p>
            </div>
            <CloudSourceHint
              onPicked={(r) => loadFile(new File([r.buffer], r.name, { type: 'application/pdf' }))}
              className="mb-6"
            />
            {error && <p role="alert" className="text-red-600 text-sm -mt-3 mb-6">{error}</p>}
          </>
        )}

        {pdfDoc && (
          <>
            {/* File info */}
            <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6 flex items-center gap-3">
              <div className="w-8 h-11 rounded border border-gray-200 overflow-hidden shrink-0 bg-white">
                <PageCanvas pdfDoc={pdfDoc} pageNumber={1} scale={0.08} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{fileName}</p>
                <p className="text-xs text-gray-500">
                  {numPages} page{numPages !== 1 ? 's' : ''} · {formatFileSize(fileSize)}
                </p>
              </div>
              <button
                onClick={handleBrowse}
                className="text-sm text-brand-blue hover:underline font-medium"
              >
                Change file
              </button>
            </div>

            {/* Analysis card */}
            {analyzing && (
              <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
                <div className="flex items-center gap-3">
                  <Loader2 size={20} className="animate-spin text-brand-blue" />
                  <span className="text-sm text-gray-600">Analyzing PDF...</span>
                </div>
              </div>
            )}

            {analysis && !result && (
              <>
                {/* Already flat banner */}
                {nothingToFlatten && (
                  <div className="bg-white rounded-xl border border-green-200 p-5 mb-6 flex items-center gap-3">
                    <FileCheck size={24} className="text-green-500 shrink-0" />
                    <div>
                      <p className="text-sm font-semibold text-gray-900">This PDF is already flat</p>
                      <p className="text-xs text-gray-500">No form fields, annotations, or JavaScript detected.</p>
                    </div>
                  </div>
                )}

                {/* Analysis results */}
                {!nothingToFlatten && (
                <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6">
                  <p className="text-sm font-medium text-gray-900 mb-3">Analysis</p>
                  <div className="grid grid-cols-2 gap-3">
                    <AnalysisRow
                      label="Form fields"
                      value={analysis.formFields > 0 ? `${analysis.formFields} detected` : 'None'}
                      active={analysis.formFields > 0}
                    />
                    <AnalysisRow
                      label="Annotations"
                      value={analysis.annotations > 0 ? `${analysis.annotations} detected` : 'None'}
                      active={analysis.annotations > 0}
                    />
                    <AnalysisRow
                      label="JavaScript"
                      value={analysis.hasJS ? 'Detected' : 'None found'}
                      active={analysis.hasJS}
                    />
                    <AnalysisRow
                      label="Transparency"
                      value="Not detected"
                      active={false}
                    />
                  </div>
                </div>
                )}

                {/* Options */}
                <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6">
                  <p className="text-sm font-medium text-gray-900 mb-3">Options</p>
                  <div className="space-y-3">
                    <OptionCheckbox
                      checked={flattenForms}
                      onChange={setFlattenForms}
                      label="Flatten Form Fields"
                      description="Bakes form field values into static text"
                      disabled={nothingToFlatten}
                    />
                    <OptionCheckbox
                      checked={flattenAnnotations}
                      onChange={setFlattenAnnotations}
                      label="Flatten Annotations"
                      description="Converts annotated pages to images — text on those pages won't stay selectable"
                      disabled={nothingToFlatten}
                    />
                    <OptionCheckbox
                      checked={removeJS}
                      onChange={setRemoveJS}
                      label="Remove JavaScript"
                      description="Strips embedded JS actions"
                      disabled={nothingToFlatten}
                    />
                    <OptionCheckbox
                      checked={reduceFileSize}
                      onChange={setReduceFileSize}
                      label="Reduce File Size"
                      description="Applies compression after flattening"
                    />
                  </div>
                </div>

                {/* Action card */}
                <div className="bg-white rounded-xl border border-gray-200 p-6">
                  {error && <p className="text-red-600 text-sm mb-4">{error}</p>}

                  {processing && (
                    <div className="mb-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Loader2 size={16} className="animate-spin text-brand-blue" />
                        <span className="text-sm text-gray-600">
                          {progressLabel || `${Math.round(progress * 100)}%`}
                        </span>
                      </div>
                      <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-brand-blue rounded-full transition-all duration-200"
                          style={{ width: `${progress * 100}%` }}
                        />
                      </div>
                    </div>
                  )}

                  <button
                    onClick={handleFlatten}
                    disabled={!actionEnabled}
                    className="w-full flex items-center justify-center gap-2 px-5 py-3 bg-brand-blue text-white rounded-lg font-medium hover:bg-brand-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Layers size={18} />
                    Flatten PDF
                  </button>
                </div>
              </>
            )}

            {/* Results — browser fallback saved nothing new */}
            {result && nothingWasFlattened && (
              <div className="bg-white rounded-xl border border-amber-200 p-6 text-center">
                <AlertTriangle size={40} className="mx-auto mb-3 text-amber-500" />
                <p className="text-lg font-semibold text-gray-900 mb-1">
                  Nothing was flattened
                </p>
                <p className="text-sm text-gray-500 mb-6">
                  This file couldn't be processed in the browser. Check your connection and
                  try again (the server engine handles more files), or use the desktop app.
                </p>
                <button
                  onClick={() => setResult(null)}
                  className="px-5 py-2.5 bg-brand-blue text-white rounded-lg font-medium hover:bg-brand-dark transition-colors"
                >
                  Try again
                </button>
              </div>
            )}

            {/* Results */}
            {result && !nothingWasFlattened && (
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                {/* Size comparison */}
                <div className="text-center mb-6">
                  <div className="flex items-center justify-center gap-3 text-2xl font-bold text-gray-900 mb-1">
                    <span>{formatFileSize(fileSize)}</span>
                    <ArrowRight size={20} className="text-gray-400" />
                    <span className={reduction > 0 ? 'text-green-600' : 'text-gray-900'}>
                      {formatFileSize(result.size)}
                    </span>
                  </div>
                  {reduction > 0 ? (
                    <p className="text-sm font-medium text-green-600">{reduction}% smaller</p>
                  ) : (
                    <p className="text-sm text-gray-500">Flattened successfully</p>
                  )}
                  {result.rasterized && (
                    <p className="mt-2 text-xs text-amber-600">
                      Annotated pages were converted to images by the browser engine. Text on
                      those pages is no longer selectable.
                    </p>
                  )}
                </div>

                {/* Visual size bar */}
                {reduction > 0 && (
                  <div className="mb-6">
                    <div className="flex items-center gap-2 text-xs text-gray-500 mb-1.5">
                      <span>Flattened</span>
                      <div className="flex-1" />
                      <span>Original</span>
                    </div>
                    <div className="relative h-6 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className="absolute inset-y-0 left-0 bg-green-500 rounded-full transition-all duration-500"
                        style={{ width: `${sizeBarWidth}%` }}
                      />
                    </div>
                  </div>
                )}

                {/* Action buttons */}
                <div className="flex gap-3">
                  <button
                    onClick={openInViewer}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-brand-blue text-white rounded-lg font-medium hover:bg-brand-dark transition-colors"
                  >
                    <Eye size={16} />
                    Open in Viewer
                  </button>
                  <button
                    onClick={handleDownload}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
                  >
                    <Download size={16} />
                    Download
                  </button>
                </div>

                <button
                  onClick={() => setResult(null)}
                  className="w-full mt-3 text-sm text-gray-500 hover:text-gray-700 py-2 transition-colors"
                >
                  Flatten another file
                </button>
              </div>
            )}
          </>
        )}
      </div>
      <FreeTierBanner open={showFreeTierModal} onClose={() => setShowFreeTierModal(false)} />
    </div>
    </ProGate>
  );
}

function AnalysisRow({ label, value, active }) {
  return (
    <div className="flex items-center gap-2 p-2.5 rounded-lg bg-gray-50">
      <CheckCircle
        size={16}
        className={active ? 'text-amber-500 shrink-0' : 'text-green-500 shrink-0'}
      />
      <div className="min-w-0">
        <p className="text-xs text-gray-500">{label}</p>
        <p className={`text-sm font-medium ${active ? 'text-amber-700' : 'text-gray-600'}`}>
          {value}
        </p>
      </div>
    </div>
  );
}

function OptionCheckbox({ checked, onChange, label, description, disabled }) {
  return (
    <label className={`flex items-start gap-2.5 ${disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="w-4 h-4 mt-0.5 rounded border-gray-300 text-brand-blue focus:ring-brand-blue disabled:cursor-not-allowed"
      />
      <div>
        <p className="text-sm font-medium text-gray-700">{label}</p>
        <p className="text-xs text-gray-500">{description}</p>
      </div>
    </label>
  );
}
